import type {
  CreateCommentInput,
  CreateTicketInput,
  Paginated,
  TicketDetail,
  TicketListItem,
} from '@servicedesk/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/** Published, externally-visible knowledge base article, as served to end users. */
export interface KbPortalArticle {
  title: string;
  slug: string;
  body: string;
  category: string | null;
  updatedAt: string;
}

export function useKbArticles(q?: string) {
  const query = q ? `?q=${encodeURIComponent(q)}` : '';
  return useQuery({
    queryKey: ['kb', 'list', q ?? ''],
    queryFn: () => api<KbPortalArticle[]>(`/api/portal/kb${query}`),
  });
}

export function useKbArticle(slug: string | undefined) {
  return useQuery({
    queryKey: ['kb', 'article', slug],
    queryFn: () => api<KbPortalArticle>(`/api/portal/kb/${slug}`),
    enabled: Boolean(slug),
  });
}

export function useMyTickets() {
  return useQuery({
    queryKey: ['my-tickets'],
    queryFn: () => api<Paginated<TicketListItem>>('/api/portal/me/tickets'),
  });
}

export function useMyTicket(id: string | undefined) {
  return useQuery({
    queryKey: ['my-ticket', id],
    queryFn: () => api<TicketDetail>(`/api/portal/me/tickets/${id}`),
    enabled: Boolean(id),
  });
}

export function useRaiseTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTicketInput) =>
      api<TicketDetail>('/api/portal/me/tickets', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-tickets'] }),
  });
}

export function useReply(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCommentInput) =>
      api(`/api/portal/me/tickets/${id}/comments`, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-ticket', id] }),
  });
}
